import { Alert, Button, Empty, Popconfirm, Select } from 'antd';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { computerReleaseNotesApi } from '@/services/computerReleaseNotesApi';
import type { ComputerReleaseDraft } from '@/services/computerReleaseNotesClient';
import { ComputerReleaseDraftStore } from '@/stores/computerReleaseDraftStore';

export const ComputerReleaseDraftPicker = observer(
  function ComputerReleaseDraftPicker({
    disabled,
    hasContent,
    onApply,
  }: {
    disabled: boolean;
    hasContent: boolean;
    onApply: (draft: ComputerReleaseDraft) => void;
  }) {
    const [store] = useState(
      () => new ComputerReleaseDraftStore(computerReleaseNotesApi),
    );

    useEffect(() => {
      void store.refresh();
      return () => store.dispose();
    }, [store]);

    const detail = store.detail;
    return (
      <section
        className="space-y-4 rounded-lg border bg-white p-6"
        aria-label="Computer 发布草稿"
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-base font-medium">Computer 发布草稿</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              选择成功的 Production 发布草稿，查看后载入下方编辑区。
            </p>
          </div>
          <Button
            loading={store.loading}
            disabled={disabled}
            onClick={() => void store.refresh()}
          >
            刷新版本
          </Button>
        </div>
        {store.listError ? (
          <Alert
            type="error"
            showIcon
            title="获取发布版本失败"
            description={store.listError}
          />
        ) : null}
        {store.drafts.length ? (
          <div className="flex flex-wrap gap-3">
            <Select
              aria-label="选择 Computer 发布版本"
              className="min-w-0 flex-1 basis-64"
              placeholder="选择一个带有草稿的版本"
              value={store.selectedVersion || undefined}
              options={store.drafts.map((draft) => ({
                value: draft.version,
                label: `${draft.version} · ${draft.headline}`,
              }))}
              onChange={(version) => void store.select(version)}
              disabled={disabled}
              loading={store.detailLoading}
            />
            {store.nextPageToken ? (
              <Button
                disabled={disabled}
                loading={store.loading}
                onClick={() => void store.loadMore()}
              >
                更多版本
              </Button>
            ) : null}
          </div>
        ) : !store.loading && !store.listError ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="还没有收到发布草稿。成功发布并启用草稿推送后，可在这里刷新查看。"
          />
        ) : null}
        <p className="text-xs text-muted-foreground">
          版本按草稿收录时间排列。选择草稿后，仍需手动确认发布更新消息。
        </p>
        {store.detailError ? (
          <Alert
            type="error"
            showIcon
            title="读取版本草稿失败"
            description={store.detailError}
            action={
              <Button
                disabled={disabled}
                onClick={() => void store.select(store.selectedVersion)}
              >
                重试
              </Button>
            }
          />
        ) : null}
        {store.detailLoading ? (
          <output className="block text-sm text-muted-foreground">
            正在读取版本草稿…
          </output>
        ) : null}
        {detail ? (
          <div className="space-y-4 border-t pt-4">
            <div className="flex flex-wrap justify-between gap-3 text-sm text-muted-foreground">
              <span>
                Computer {detail.version} ·{' '}
                {detail.summarySource === 'model' ? 'AI 摘要' : '提交标题摘要'}
              </span>
              <a
                href={detail.runUrl}
                target="_blank"
                rel="noreferrer"
                className="underline underline-offset-4"
              >
                查看 GitHub 发布任务
              </a>
            </div>
            <p className="text-xs text-muted-foreground">
              发布时间：
              {new Date(detail.releasePublishedAt).toLocaleString('zh-CN')} ·
              提交 {detail.sourceSha.slice(0, 12)}
            </p>
            <h3 className="break-words font-medium">{detail.headline}</h3>
            <pre className="max-h-64 overflow-y-auto whitespace-pre-wrap break-words font-sans text-sm leading-6 [overflow-wrap:anywhere]">
              {detail.bodyMarkdown}
            </pre>
            {hasContent ? (
              <Popconfirm
                title="替换编辑区的内容？"
                description="当前标题、正文和版本标记将被这个草稿替换。"
                okText="替换内容"
                cancelText="保留当前内容"
                onConfirm={() => onApply(detail)}
                disabled={disabled}
              >
                <Button disabled={disabled}>载入编辑区</Button>
              </Popconfirm>
            ) : (
              <Button disabled={disabled} onClick={() => onApply(detail)}>
                载入编辑区
              </Button>
            )}
          </div>
        ) : null}
      </section>
    );
  },
);
